/**
 * Le contrat du journal d'audit, côté navigateur (`B-16`, `EX-BO-004`).
 *
 * **Ce fichier ne décide de rien.** Il transporte, et il déclare ce que le
 * serveur rend — les types suivent `BackOfficeMessages.AuditEntryView`, sans
 * rien y ajouter. Un contrôle confronte champ par champ cette déclaration au
 * **fichier Java lui-même** : recopier un contrat, c'est en faire une seconde
 * source de vérité qui vieillira.
 *
 * ## Deux lectures, et c'est la seconde qui fait le journal
 *
 * `GET /api/backoffice/audit` relit **une entité désignée** : `entityType` et
 * `entityRef` sont tous deux obligatoires. C'est une lecture par clé — utile
 * quand on sait ce qu'on cherche, inutilisable quand on cherche justement à le
 * découvrir. Le porteur l'a dit d'une phrase, la maquette sous les yeux :
 * « pourquoi on a besoin des champs à saisir pour voir cela ? ». L'écran
 * n'était pas en faute ; il était fidèle à sa route, et la route était fausse
 * pour le métier.
 *
 * `GET /api/backoffice/audit/entries` **liste**, et n'exige que le motif. Sans
 * fenêtre demandée, le serveur applique la journée courante — c'est lui qui
 * écrit les `occurred_at`, c'est donc lui qui sait ce qu'« aujourd'hui » veut
 * dire pour ce journal — et il **rend** la fenêtre appliquée. Les quatre
 * filtres du dessin y sont : `from`, `to`, `actorId`, `action`. La pagination
 * aussi : `page`, et le serveur rend le **total**, qui n'est pas la taille de
 * la page.
 *
 * **Ce que le serveur ne sert toujours pas**, et l'écran le dit plutôt que de
 * l'inventer : les **cinq pastilles** — Toutes · Décisions · Paramètres ·
 * Consultations · Sécurité — groupent les codes d'action en familles, et cette
 * classification n'existe nulle part. `admin.bo_action` porte bien un `module`
 * et un `is_write`, mais ce sont seize modules, pas quatre familles — et
 * surtout cette table ne connaît que les actions *habilitables* : `BO_LOGIN`,
 * `BO_LOGIN_DENIED`, `BO_ADDRESS_DENIED` et les variantes suffixées sont
 * écrites au journal sans y figurer. Un groupement bâti dessus tairait
 * précisément la famille « Sécurité ». Les deux indicateurs qui en dérivent —
 * « Décisions », « Modifications de paramètre » — sont omis pour la même
 * raison. Le quatrième, « Intégrité — chaînage vérifié », l'est pour une
 * raison plus grave : la table ne porte **aucune** colonne d'empreinte, rien ne
 * peut le calculer, et l'afficher fabriquerait une assurance d'intégrité que
 * personne ne vérifie.
 *
 * **Lire le journal est soi-même un acte journalisé.** `reason` est
 * obligatoire — `BackOfficeService.requireReason` refuse un motif vide — et le
 * serveur écrit une entrée `AUDIT_LOG_VIEW` à chaque consultation. C'est la
 * raison pour laquelle le motif est **saisi** ici et non figé comme sur la
 * revue KYC : un auditeur ouvre une trace pour une raison qui lui est propre —
 * la maquette écrit « Motif déclaré : ticket TK-11840 » —, là où l'analyste
 * KYC dépile toujours la même file.
 */

import type { ApiClient } from './api-client.js';

/** Le chemin de la lecture **par entité**, tel que `BackOfficeApi` le sert. */
export const auditLogPath = '/api/backoffice/audit';

/** Le chemin du **listage**, tel que `BackOfficeApi` le sert. */
export const auditJournalPath = '/api/backoffice/audit/entries';

/**
 * Le motif d'audit du **listage** — une constante, et non une saisie.
 *
 * C'est la conduite de la file de double validation et des quatre lectures du
 * rapprochement, et la raison est la même : l'auditeur ouvre le journal
 * toujours pour la même raison — le consulter. Une invite par ouverture
 * coûterait une frappe pour une phrase identique, et l'écran ne pourrait plus
 * s'ouvrir sur le journal, ce que ce chantier existe pour permettre.
 *
 * **Ce qui distingue une consultation d'une autre est déjà journalisé** : le
 * serveur écrit la fenêtre, les deux filtres, la page et le nombre d'entrées
 * révélées. Le motif n'a pas à redire ce que les critères disent.
 *
 * La lecture **par entité**, elle, garde son motif saisi : un auditeur ouvre la
 * trace d'une entité pour une raison qui lui est propre — la maquette écrit
 * « Motif déclaré : ticket TK-11840 ». Confondre les deux inscrirait
 * « consultation du journal » comme motif de l'examen d'un dossier nommé.
 */
export const auditJournalReason = 'consultation-du-journal';

/**
 * Les **sept types d'entité** que le serveur journalise, dans l'ordre où
 * `BackOfficeService.java` les déclare.
 *
 * C'est une liste **fermée**, et c'était le grief du porteur : l'écran la
 * connaissait si bien qu'il l'énumérait dans son propre libellé — « Type
 * d'entité — CUSTOMER, KYC_REVIEW, BO_USER… » — puis demandait à l'opérateur de
 * la taper. Une liste fermée offerte en texte libre laisse saisir ce qui
 * n'existe pas, et le refus arrive du serveur, après coup, sans dire ce qui
 * existait.
 *
 * **Aucune route ne sert cette liste**, et aucune n'est ouverte pour elle : le
 * serveur les porte en constantes Java — `ENTITY_BO_USER`, `ENTITY_BO_ACCESS`,
 * `ENTITY_APPROVAL`, `ENTITY_CUSTOMER`, `ENTITY_KYC_REVIEW`,
 * `ENTITY_RECONCILIATION`, `ENTITY_COMMISSIONS` — et rien ne les expose. C'est
 * donc un **miroir**, avec tout ce qu'un miroir coûte : le jour où une huitième
 * constante apparaît côté serveur, ce tableau devient faux **en silence**.
 *
 * C'est exactement ce qu'un contrôle doit refuser, et c'est ce qu'il fait :
 * `test/app/audit-log-maquette.test.tsx` **relit `BackOfficeService.java`**,
 * en extrait les constantes `ENTITY_*` et confronte leurs valeurs à ce tableau,
 * dans l'ordre. Une constante ajoutée, retirée ou renommée fait rougir la
 * suite — c'est la conduite déjà tenue pour `AuditEntryView` (`D-35`, `D-45`),
 * appliquée cette fois à une énumération plutôt qu'à un `record`.
 *
 * Les codes ne sont pas traduits, et ne doivent pas l'être : ce sont ceux que
 * la colonne « Cible » de l'écran affiche, parce que ce sont ceux que le
 * serveur rend. Une liste qui proposerait « Client » sous une table qui répond
 * « CUSTOMER » serait deux vocabulaires pour une seule chose.
 */
export const auditEntityTypes: readonly string[] = [
  'BO_USER',
  'BO_ACCESS',
  'APPROVAL_REQUEST',
  'CUSTOMER',
  'KYC_REVIEW',
  'RECONCILIATION',
  'COMMISSIONS',
  // Une huitième, apportée par la surveillance des transactions (V35) : une
  // consultation de la file d'alertes, d'une investigation ou du reporting ne
  // désigne pas une alerte — elle ouvre un dispositif —, et l'entrée qu'elle
  // laisse nomme donc `AML`. Valeur AJOUTÉE à sa place, celle qu'occupe la
  // constante `ENTITY_AML` côté serveur ; la liste n'est jamais recomposée (G3).
  //
  // **C'est le miroir qui a parlé le premier.** L'epic 11 a ajouté la constante
  // côté serveur, et `audit-log-maquette.test.tsx` a rougi avant qu'un écran ne
  // lise la liste : sans lui, le journal aurait offert huit types d'entité là
  // où le serveur en journalise neuf, et la trace de la surveillance aurait été
  // introuvable par le filtre exact.
  'AML',
  // Une neuvième, apportée par la gouvernance tarifaire (V39). Le code est
  // « TARIFICATION » et non « PRICING » : les modules du back-office sont
  // nommés en français depuis l'annexe F — `APPROBATIONS`, `CONFORMITE`,
  // `RECONCILIATION`, `RESEAU` —, et une consultation tarifaire laisse une
  // entrée qui nomme son module. L'angliciser ici en ferait deux vocabulaires
  // pour une seule chose, ce que le commentaire de tête de cette liste refuse.
  //
  // **Et c'est le miroir qui a parlé, une troisième fois.** L'epic 10 a ajouté
  // `ENTITY_PRICING` côté serveur ; cette liste est restée à onze valeurs, et
  // le filtre exact n'aurait jamais retrouvé la trace d'une décision tarifaire
  // — le geste le plus cher du back-office. Valeur AJOUTÉE à sa place, celle
  // qu'occupe la constante côté serveur ; la liste n'est jamais recomposée (G3).
  'TARIFICATION',
  // Une dixième, apportée par le support et les litiges (V36) — le chantier
  // voisin, en cours au moment où ceci s'écrit. La constante `ENTITY_SUPPORT`
  // existe côté serveur ; le miroir la porte donc, faute de quoi la suite du
  // back-office rougit et le journal offrirait un type d'entité de moins que ce
  // que le serveur journalise. L'écran du support, lui, viendra avec son epic.
  'SUPPORT',
  // Une onzième, apportée par les états et le pilotage (V34) : une production
  // d'état laisse une entrée qui nomme « REPORTING ». Valeur AJOUTÉE à sa place
  // — celle qu'occupe la constante côté serveur —, la liste jamais recomposée.
  'REPORTING',
  // Une douzième, apportée par le tableau de bord des envois (story 3.11, V41) :
  // une consultation des taux par canal et des dépôts SMS ouvre un DISPOSITIF,
  // pas une fiche — elle ne désigne ni un client, ni un message —, et l'entrée
  // qu'elle laisse nomme donc `NOTIFICATIONS`. Valeur AJOUTÉE à sa place, celle
  // qu'occupe la constante côté serveur ; la liste n'est jamais recomposée (G3).
  //
  // L'ÉCRAN N'EXISTE PAS ENCORE, et la valeur est là quand même : le miroir
  // suit les constantes du SERVEUR, pas les écrans du navigateur. C'est ce que
  // 'SUPPORT' fait déjà, pour la même raison.
  'NOTIFICATIONS',
  // Une treizième, apportée par la route de listage : un listage ne désigne
  // aucune entité, et l'entrée qu'il laisse nomme donc le JOURNAL lui-même.
  // Une valeur AJOUTÉE, la liste jamais recomposée (G3) — et c'est le miroir
  // qui a fonctionné : la constante `ENTITY_AUDIT_LOG` ajoutée côté serveur a
  // fait rougir cette liste avant qu'un écran ne la lise.
  'AUDIT_LOG',
  // Une quatorzième, apportée par la recherche de transactions du back-office :
  // une recherche n'ouvre AUCUNE fiche — elle rend ce que la fenêtre et les
  // filtres retiennent —, et l'entrée qu'elle laisse nomme donc le REGISTRE.
  // La valeur est `TRANSACTIONS`, le `module` que `admin.bo_action` donne à
  // `TRANSACTION_VIEW`, et non `TRANSACTION` au singulier : c'est la discipline
  // posée en soldant `D-80`, et celle que `RESEAU` et `TARIFICATION` suivent
  // déjà. Valeur AJOUTÉE à sa place, celle qu'occupe la constante côté serveur ;
  // la liste n'est jamais recomposée (G3).
  'TRANSACTIONS',
];

/**
 * Une entrée du journal, exactement telle que le serveur la rend.
 *
 * `actorId` est un **identifiant**, pas un nom d'affichage : le serveur rend
 * l'`UUID` de `admin.bo_user`. La maquette dessine « A. Diop » ; aucune route
 * ne sert ce nom, et l'écrire ici l'inventerait. `action`, `entityType` et
 * `actorType` sont de même des **codes** — `AUDIT_LOG_VIEW`, `CUSTOMER`,
 * `BO_USER` — et non les phrases françaises du dessin.
 *
 * Cinq champs peuvent manquer, et chacun pour une raison qui se lit :
 * `actorId` est absent quand l'acteur n'est pas un agent — la maquette montre
 * elle-même une ligne « système » avec l'adresse « interne », donc ni l'acteur
 * ni l'adresse ne peuvent être supposés humains ; `beforeValue` et
 * `afterValue` sont absents d'une consultation, qui ne modifie rien ; `reason`
 * et `ipAddress` peuvent l'être d'une écriture faite hors requête.
 */
export interface AuditEntry {
  readonly id: number;
  readonly actorType: string;
  readonly actorId: string | null;
  readonly action: string;
  readonly entityType: string;
  readonly entityRef: string;
  readonly beforeValue: string | null;
  readonly afterValue: string | null;
  readonly reason: string | null;
  readonly ipAddress: string | null;
  readonly occurredAt: string;
}

/**
 * Un acteur présent dans la fenêtre, tel que le serveur le rend.
 *
 * `actorId` manque quand l'acteur n'est pas un agent : le journal écrit aussi
 * des lignes `SYSTEM`, et la maquette en montre elle-même une, avec l'adresse
 * « interne ». Aucun nom d'affichage n'est servi, ici pas plus qu'ailleurs.
 */
export interface AuditActor {
  readonly actorType: string;
  readonly actorId: string | null;
}

/**
 * Une **page** du journal, exactement telle que le serveur la rend.
 *
 * `total` est ce que la fenêtre et les filtres retiennent **en tout**, jamais
 * la taille de la page : la maquette écrit « 6 entrées sur 1 842 du jour », et
 * un compte qui vaudrait la taille de la page serait un chiffre faux affiché
 * comme un fait — une page rendue pleine ne dit pas s'il en reste une ou mille.
 *
 * `from` et `to` sont les bornes **effectivement appliquées**, y compris quand
 * l'écran n'en a demandé aucune. Les ignorer obligerait l'écran à réafficher ce
 * qu'il croit avoir demandé, c'est-à-dire à deviner la journée qu'il montre.
 *
 * `actions` et `actors` sont les valeurs **réellement présentes** dans la
 * fenêtre — c'est ce qui permet aux deux filtres à liste fermée du dessin
 * d'être bâtis sur de la *donnée* et non sur un miroir de constantes Java qui
 * vieillirait en silence. Elles sont calculées sur la fenêtre **seule** : une
 * liste qui rétrécirait à mesure qu'on filtre deviendrait vide au premier
 * choix, et l'on ne pourrait plus jamais élargir.
 */
export interface AuditPage {
  readonly entries: readonly AuditEntry[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  readonly from: string;
  readonly to: string;
  readonly actions: readonly string[];
  readonly actors: readonly AuditActor[];
}

/**
 * Ce qu'un listage retient. **Aucun champ n'est obligatoire**, et c'est tout
 * l'objet de cette route.
 */
export interface AuditJournalQuery {
  /** Journée de début, en ISO `AAAA-MM-JJ`. Absente : la journée courante. */
  readonly from?: string;
  /** Journée de fin, incluse. Absente : la journée courante. */
  readonly to?: string;
  /** L'identifiant de l'agent filtré. Absent : tous. */
  readonly actorId?: string;
  /** Le code d'action filtré. Absent : tous. */
  readonly action?: string;
  /** La page demandée, à partir de 1. Absente : la première. */
  readonly page?: number;
}

/**
 * Liste le journal.
 *
 * Aucune capture d'erreur, pour la raison déjà écrite sous
 * {@link fetchAuditLog} : rendre une liste vide ferait confondre « le journal
 * ne porte rien ce jour-là » — un fait — avec « le serveur a refusé », qui n'en
 * est pas un. Sur un journal d'audit, la confusion se lit comme une absence de
 * trace, c'est-à-dire l'inverse de ce qui s'est passé.
 */
export async function fetchAuditJournal(
  client: ApiClient,
  query: AuditJournalQuery = {},
  reason: string = auditJournalReason,
): Promise<AuditPage> {
  const parametres = new URLSearchParams({ reason });
  // Un filtre vide n'est PAS envoyé vide : `action=` serait un critère que le
  // serveur devrait interpréter, là où son absence dit sans ambiguïté « tous ».
  if (query.from !== undefined && query.from !== '') parametres.set('from', query.from);
  if (query.to !== undefined && query.to !== '') parametres.set('to', query.to);
  if (query.actorId !== undefined && query.actorId !== '') {
    parametres.set('actorId', query.actorId);
  }
  if (query.action !== undefined && query.action !== '') parametres.set('action', query.action);
  if (query.page !== undefined) parametres.set('page', String(query.page));
  const page = await client.request<AuditPage>(`${auditJournalPath}?${parametres.toString()}`);
  return exigerUnePage(page);
}

/**
 * Refuse ce qui n'est **pas** une page du journal.
 *
 * Le contrat servi est éprouvé champ par champ contre le `record` Java, mais
 * cela vaut pour ce que le serveur *déclare* ; à l'exécution, une réponse d'une
 * autre forme — une route mal branchée, un mandataire qui rend sa propre page —
 * arriverait ici sans rien dire. L'écran s'écroulerait alors sur une lecture de
 * `entries.length`, et l'opérateur verrait un écran blanc **sur le journal
 * d'audit**, c'est-à-dire l'endroit où l'absence d'information est le plus
 * coûteuse.
 *
 * Le refus est donc explicite, et il devient un message : mieux vaut « le
 * serveur a répondu quelque chose d'inutilisable » qu'une page vide, qui se
 * lirait comme « rien ne s'est passé ».
 */
function exigerUnePage(page: AuditPage): AuditPage {
  if (!Array.isArray(page?.entries) || typeof page?.total !== 'number') {
    throw new Error(
      'ÉCHEC : la réponse du listage n’est pas une page du journal — ' +
        '`entries` et `total` sont ce qui la définit.',
    );
  }
  return page;
}

/** Ce qu'une consultation du journal exige — les trois sont obligatoires. */
export interface AuditLogQuery {
  /** Le type de l'entité tracée : `CUSTOMER`, `KYC_REVIEW`, `BO_USER`… */
  readonly entityType: string;
  /** Sa référence, telle que le journal l'a écrite. */
  readonly entityRef: string;
  /** Pourquoi cette trace est consultée. Le serveur la journalise avec. */
  readonly reason: string;
}

/**
 * Lit la trace d'une entité.
 *
 * Aucune capture d'erreur : l'`ApiError` du client porte déjà sa nature et sa
 * clé de message. L'avaler pour rendre une liste vide ferait confondre « cette
 * entité n'a aucune trace » — un fait — avec « le serveur a refusé », qui n'en
 * est pas un ; sur un journal d'audit, la confusion se lirait comme une
 * absence de trace, c'est-à-dire l'inverse de ce qui s'est passé.
 */
export async function fetchAuditLog(
  client: ApiClient,
  query: AuditLogQuery,
): Promise<readonly AuditEntry[]> {
  const parametres = new URLSearchParams({
    entityType: query.entityType,
    entityRef: query.entityRef,
    reason: query.reason,
  });
  return client.request<readonly AuditEntry[]>(`${auditLogPath}?${parametres.toString()}`);
}
